import { useEffect, useState } from "react";
import {
    Table,
    TableBody,
    TableCell,
    TableHeader,
    TableRow,
} from "../../ui/table";
import Badge from "../../ui/badge/Badge";
import { Edit, Trash2, AlertCircle, ExternalLink, Info } from "lucide-react";
import MoreButton from "../../common/MoreButton";
import { useNavigate } from "react-router";
import api from "../../../services/api";
import authService from "../../../services/authService";
import Pagination from "../../ui/pagination/Pagination";
import ComponentCard from "../../common/ComponentCard";
import Button from "../../ui/button/Button";
import Modal from "../../common/Modal";
import Alert from "../../ui/alert/Alert";

const SELLYO_MEMBERS_URL = "https://members.sellyo.com.br";

const openSellyoMembers = () => {
    window.open(SELLYO_MEMBERS_URL, "_blank", "noopener,noreferrer");
};

interface SellyoMembersIntegration {
    id: string;
    integrationId: string;
    companyId: string;
    name: string;
    provider: string;
    productId: string;
    productName: string;
    active: boolean;
    settingsData?: {
        showcaseIds?: string[];
        showcaseNames?: string[];
    } | null;
    createdAt: string;
    updatedAt: string;
}

export default function SellyoMembersTable() {
    const navigate = useNavigate();
    const [integrations, setIntegrations] = useState<SellyoMembersIntegration[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [alertInfo, setAlertInfo] = useState<{
        show: boolean;
        type: "success" | "error" | "warning" | "info";
        message: string;
        title: string;
    } | null>(null);

    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 10;

    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [integrationToDelete, setIntegrationToDelete] = useState<string | null>(null);
    const [deleteLoading, setDeleteLoading] = useState(false);

    const fetchIntegrations = async () => {
        try {
            setLoading(true);
            setError(null);
            const companyId = authService.getCompanyId();
            const response = await api.get("/api/membership", {
                params: { provider: "sellyo_members", companyId },
            });
            if (response.data?.success) {
                setIntegrations(response.data.data);
            } else {
                setIntegrations([]);
            }
        } catch (err: unknown) {
            const message = err instanceof Error ? err.message : "Erro ao carregar integrações";
            setError(message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchIntegrations();
    }, []);

    const handleDelete = async () => {
        if (!integrationToDelete) return;
        setDeleteLoading(true);
        try {
            await api.delete(`/api/membership/${integrationToDelete}`);
            setDeleteModalOpen(false);
            setIntegrationToDelete(null);
            fetchIntegrations();
            setAlertInfo({ show: true, type: "success", title: "Sucesso!", message: "Integração excluída com sucesso." });
            setTimeout(() => setAlertInfo(null), 3000);
        } catch {
            setAlertInfo({ show: true, type: "error", title: "Erro!", message: "Erro ao excluir integração." });
        } finally {
            setDeleteLoading(false);
        }
    };

    const indexOfLast = currentPage * itemsPerPage;
    const indexOfFirst = indexOfLast - itemsPerPage;
    const currentItems = integrations.slice(indexOfFirst, indexOfLast);
    const totalPages = Math.ceil(integrations.length / itemsPerPage);

    if (error) {
        return <div className="p-4 text-center dark:text-white/90">Erro: {error}</div>;
    }

    if (integrations.length === 0 && !loading) {
        return (
            <ComponentCard title="Sellyo Members — Integrações">
                <div className="rounded-xl border border-brand-100 bg-brand-50/60 p-5 dark:border-brand-500/20 dark:bg-brand-500/10">
                    <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
                        <div className="flex gap-3">
                            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-brand-500/10 text-brand-600 dark:bg-brand-500/20 dark:text-brand-400">
                                <Info className="h-5 w-5" />
                            </div>
                            <div>
                                <h4 className="text-sm font-semibold text-gray-800 dark:text-white/90">
                                    Antes de criar uma integração
                                </h4>
                                <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">
                                    Para usar o Sellyo Members você precisa ter pelo menos uma área de membros / vitrine criada e configurada no painel.
                                </p>
                                <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">
                                    A integração aqui só conecta seus produtos às vitrines que já existem por lá.
                                </p>
                            </div>
                        </div>
                        <Button
                            size="sm"
                            variant="outline"
                            onClick={openSellyoMembers}
                            startIcon={<ExternalLink className="h-4 w-4" />}
                        >
                            Acessar Sellyo Members
                        </Button>
                    </div>
                </div>

                <div className="mt-6 flex flex-col items-center justify-center gap-4 py-8">
                    <p className="text-center text-gray-500 dark:text-white/60">
                        Você ainda não possui integrações Sellyo Members cadastradas.
                    </p>
                    <div className="flex flex-wrap items-center justify-center gap-3">
                        <Button
                            size="md"
                            variant="outline"
                            onClick={openSellyoMembers}
                            startIcon={<ExternalLink className="h-4 w-4" />}
                        >
                            Criar área de membros
                        </Button>
                        <Button
                            size="md"
                            variant="primary"
                            onClick={() => navigate("/apps/sellyo-members/new")}
                        >
                            Adicionar Integração
                        </Button>
                    </div>
                </div>
            </ComponentCard>
        );
    }

    return (
        <ComponentCard title="Sellyo Members — Integrações">
            <div className="mb-6 rounded-xl border border-brand-100 bg-brand-50/60 p-4 dark:border-brand-500/20 dark:bg-brand-500/10">
                <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                    <div className="flex gap-3">
                        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-brand-500/10 text-brand-600 dark:bg-brand-500/20 dark:text-brand-400">
                            <Info className="h-4 w-4" />
                        </div>
                        <p className="text-sm text-gray-600 dark:text-gray-300">
                            Suas áreas de membros e vitrines são gerenciadas em{" "}
                            <span className="font-medium text-gray-800 dark:text-white/90">members.sellyo.com.br</span>.
                            Crie ou ajuste vitrines por lá antes de conectá-las a um produto aqui.
                        </p>
                    </div>
                    <Button
                        size="sm"
                        variant="outline"
                        onClick={openSellyoMembers}
                        startIcon={<ExternalLink className="h-4 w-4" />}
                    >
                        Acessar Sellyo Members
                    </Button>
                </div>
            </div>

            <div className="col-span-12 space-y-6">
                <div className="mb-6 flex flex-col md:flex-row justify-between items-center gap-4">
                    <div className="relative w-full md:w-1/3 lg:w-1/4">
                        <button className="absolute -translate-y-1/2 left-4 top-1/2">
                            <svg className="fill-gray-500 dark:fill-gray-400" width="20" height="20" viewBox="0 0 20 20" fill="none">
                                <path fillRule="evenodd" clipRule="evenodd" d="M3.04175 9.37363C3.04175 5.87693 5.87711 3.04199 9.37508 3.04199C12.8731 3.04199 15.7084 5.87693 15.7084 9.37363C15.7084 12.8703 12.8731 15.7053 9.37508 15.7053C5.87711 15.7053 3.04175 12.8703 3.04175 9.37363ZM9.37508 1.54199C5.04902 1.54199 1.54175 5.04817 1.54175 9.37363C1.54175 13.6991 5.04902 17.2053 9.37508 17.2053C11.2674 17.2053 13.003 16.5344 14.357 15.4176L17.177 18.238C17.4699 18.5309 17.9448 18.5309 18.2377 18.238C18.5306 17.9451 18.5306 17.4703 18.2377 17.1774L15.418 14.3573C16.5365 13.0033 17.2084 11.2669 17.2084 9.37363C17.2084 5.04817 13.7011 1.54199 9.37508 1.54199Z" fill="" />
                            </svg>
                        </button>
                        <input
                            type="text"
                            placeholder="Pesquisar integração..."
                            className="dark:bg-dark-900 h-11 w-full rounded-lg border border-gray-200 bg-transparent py-2.5 pl-12 pr-4 text-sm text-gray-800 shadow-theme-xs placeholder:text-gray-400 focus:border-brand-300 focus:outline-hidden focus:ring-3 focus:ring-brand-500/10 dark:border-gray-800 dark:bg-white/[0.03] dark:text-white/90 dark:placeholder:text-white/30 dark:focus:border-brand-800"
                        />
                    </div>
                    <Button
                        size="md"
                        variant="primary"
                        onClick={() => navigate("/apps/sellyo-members/new")}
                    >
                        Adicionar Integração
                    </Button>
                </div>
            </div>

            <div className="overflow-hidden rounded-xl">
                <div className="max-w-full overflow-x-auto">
                    <Table>
                        <TableHeader className="border-b border-gray-100 dark:border-white/[0.05]">
                            <TableRow>
                                <TableCell isHeader className="px-5 py-3 font-medium text-gray-500 text-start text-theme-xs dark:text-gray-400">
                                    Nome
                                </TableCell>
                                <TableCell isHeader className="px-5 py-3 font-medium text-gray-500 text-start text-theme-xs dark:text-gray-400">
                                    Produto
                                </TableCell>
                                <TableCell isHeader className="px-5 py-3 font-medium text-gray-500 text-start text-theme-xs dark:text-gray-400">
                                    Vitrines
                                </TableCell>
                                <TableCell isHeader className="px-5 py-3 font-medium text-gray-500 text-start text-theme-xs dark:text-gray-400">
                                    Status
                                </TableCell>
                                <TableCell isHeader className="px-5 py-3 font-medium text-gray-500 text-end text-theme-xs dark:text-gray-400 pr-8">
                                    Ações
                                </TableCell>
                            </TableRow>
                        </TableHeader>

                        <TableBody className="divide-y divide-gray-100 dark:divide-white/[0.05]">
                            {!loading && currentItems.map((integration) => (
                                <TableRow key={integration.id}>
                                    <TableCell className="px-4 py-3 text-start text-theme-sm">
                                        <span className="font-medium text-gray-800 dark:text-white/90">
                                            {integration.name}
                                        </span>
                                    </TableCell>
                                    <TableCell className="px-4 py-3 text-gray-500 text-start text-theme-sm dark:text-gray-400">
                                        {integration.productName || "—"}
                                    </TableCell>
                                    <TableCell className="px-4 py-3 text-gray-500 text-start text-theme-sm dark:text-gray-400">
                                        <div className="flex flex-wrap gap-1">
                                            {(integration.settingsData?.showcaseNames?.length ?? 0) > 0 ? (
                                                integration.settingsData!.showcaseNames!.map((name, i) => (
                                                    <span
                                                        key={i}
                                                        className="inline-flex items-center rounded-full bg-brand-50 px-2 py-0.5 text-xs font-medium text-brand-700 dark:bg-brand-500/10 dark:text-brand-400"
                                                    >
                                                        {name}
                                                    </span>
                                                ))
                                            ) : (
                                                <span className="text-gray-400">—</span>
                                            )}
                                        </div>
                                    </TableCell>
                                    <TableCell className="px-4 py-3 text-start text-theme-sm">
                                        <Badge size="sm" color={integration.active ? "success" : "error"}>
                                            {integration.active ? "Ativo" : "Inativo"}
                                        </Badge>
                                    </TableCell>
                                    <TableCell className="px-4 py-3 text-end text-theme-sm pr-8">
                                        <div className="flex justify-end gap-2">
                                            <MoreButton
                                                options={[
                                                    {
                                                        label: "Editar",
                                                        onClick: () => navigate(`/apps/sellyo-members/edit/${integration.id}`),
                                                        icon: <Edit className="size-4" />,
                                                    },
                                                    {
                                                        label: "Excluir",
                                                        onClick: () => {
                                                            setIntegrationToDelete(integration.id);
                                                            setDeleteModalOpen(true);
                                                        },
                                                        icon: <Trash2 className="size-4" />,
                                                        className: "text-red-600 dark:text-red-400",
                                                    },
                                                ]}
                                            />
                                        </div>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>

                    {!loading && totalPages > 1 && (
                        <div className="p-4 border-t border-gray-100 dark:border-white/[0.05]">
                            <Pagination
                                currentPage={currentPage}
                                totalPages={totalPages}
                                onPageChange={setCurrentPage}
                            />
                        </div>
                    )}
                </div>
            </div>

            {alertInfo?.show && (
                <div className="mt-4">
                    <Alert variant={alertInfo.type} title={alertInfo.title} message={alertInfo.message} />
                </div>
            )}

            <Modal
                isOpen={deleteModalOpen}
                onClose={() => setDeleteModalOpen(false)}
                size="sm"
                onConfirm={handleDelete}
                confirmText="Excluir"
                cancelText="Cancelar"
                confirmButtonClass="bg-red-500 hover:bg-red-600 text-white"
                confirmLoading={deleteLoading}
            >
                <div className="flex flex-col items-center text-center py-4">
                    <AlertCircle className="h-16 w-16 text-red-500 mb-4" />
                    <h3 className="text-lg font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Tem certeza que deseja excluir esta integração?
                    </h3>
                    <p className="mt-1 text-sm text-gray-500 dark:text-gray-400 max-w-sm">
                        Não será possível reverter esta ação!
                    </p>
                </div>
            </Modal>
        </ComponentCard>
    );
}